import { isValid, parse } from 'date-fns';

/**
 * Try to parse a date string with a specific format
 */
export const tryParseDate = (dateString: string, format: string): Date | null => {
  const parsedDate = parse(dateString, format, new Date());
  return isValid(parsedDate) ? parsedDate : null;
};

/**
 * Try to parse a string as a date using multiple common formats
 */
export const tryParseStringAsDate = (dateString: string): Date | null => {
  if (!dateString) return null; // Return null if the date string is empty or null

  let parsedDate = tryParseDate(dateString, 'dd/MM/yyyy'); // Returns a Date object
  if (parsedDate) {
    return parsedDate; // Valid date
  }
  parsedDate = tryParseDate(dateString, 'dd.MM.yyyy');
  if (parsedDate) {
    return parsedDate; // Valid date
  }
  parsedDate = tryParseDate(dateString, 'dd-MM-yyyy');
  if (parsedDate) {
    return parsedDate; // Valid date
  }

  return new Date(dateString);
};

/**
 * Try to parse a string as a number, handling Norwegian number formatting
 */
export const tryParseAsNumber = (value: string): number | null => {
  if (!value) return null; // Return null if the value is empty or null

  const parsedNumber = parseFloat(value.replace(/\./g, '').replace(/,/g, '.'));
  return isNaN(parsedNumber) ? null : parsedNumber; // Return null if parsing fails
};

/**
 * Parse currency string to number, handling Norwegian currency formatting
 */
export const parseCurrencyToNumber = (currencyString: string): number | null => {
  if (!currencyString) return null;

  // Remove currency symbol (kr) and any non-numeric characters except for comma and period
  // Then replace spaces with nothing
  const cleanedString = currencyString.replace(/[^\d,.\s]/g, '').replace(/\s/g, '');

  // Replace comma with period for decimal point (if using comma as decimal separator)
  // This handles both "55 000 000,00" and "55.000.000,00" formats
  const standardizedString = cleanedString.replace(/\./g, '').replace(/,/g, '.');

  const parsedNumber = parseFloat(standardizedString);
  return isNaN(parsedNumber) ? null : parsedNumber;
};

/**
 * Validate phone number string
 */
export const validatePhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return '';

  const trimmed = phoneNumber.trim();

  // Check if it contains only valid phone number characters (digits, spaces, +, -, parentheses)
  const phoneRegex = /^[\d\s\+\-\(\)]+$/;

  if (phoneRegex.test(trimmed)) {
    return trimmed;
  }
  return '';
};
